/**
 * useTabPreferences
 *
 * Manages the user's list of preferred (visible) catalog entity tabs.
 * Stored in the 'user-tab-preferences' storage bucket under key 'preferredTabs'.
 *
 * An empty list means "show all tabs" (no filtering).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';

const STORAGE_BUCKET = 'user-tab-preferences';
const STORAGE_KEY = 'preferredTabs';

/** Tabs that are always visible when a user has explicit tab preferences. */
export const READ_ONLY_DEFAULT_TABS: readonly string[] = ['Overview', 'Docs'];

/** All known tab titles across all entity pages in the catalog */
export const ALL_KNOWN_TABS: string[] = [
  'Overview',
  'Docs',
  'CI/CD',
  'Kubernetes',
  'Pull Requests',
  'GitHub Issues',
  'Code Insights',
  'Dependabot',
  'Dependencies',
  'Datadog',
  'Q&A',
  'Feedback',
  'Score card',
  'DORA Metrics',
  'ADRs',
  'Tech Radar',
  'Config',
  'Definition',
  'GraphiQL',
  'ECS',
  'CodeBuild',
  'Diagram',
];

export const normalizePreferredTabs = (tabs: string[]): string[] => {
  if (tabs.length === 0) {
    return [];
  }

  const editableTabs = tabs.filter(
    tab => !READ_ONLY_DEFAULT_TABS.includes(tab),
  );

  // If only read-only defaults remain, treat it as show-all mode.
  if (editableTabs.length === 0) {
    return [];
  }

  const merged = [...editableTabs];
  for (const requiredTab of READ_ONLY_DEFAULT_TABS) {
    if (!merged.includes(requiredTab)) {
      merged.push(requiredTab);
    }
  }

  return merged;
};

export interface UseTabPreferencesReturn {
  /** User's selected preferred tab titles. Empty array = show all. */
  preferredTabs: string[];
  /** Replace the entire preferred tabs list */
  setPreferredTabs: (tabs: string[]) => Promise<void>;
  /** Whether preferences are still loading from storage */
  loading: boolean;
  /** Error message if preference save failed */
  error: string | null;
}

export function useTabPreferences(): UseTabPreferencesReturn {
  const storageBase = useApi(storageApiRef);
  // forBucket() returns a new object every call; memoize to keep a stable
  // reference so the useEffect([storageApi]) below only runs once on mount.
  const storageApi = useMemo(
    () => storageBase.forBucket(STORAGE_BUCKET),
    [storageBase],
  );
  const [preferredTabs, setPreferredTabsState] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    setLoading(true);

    try {
      const snapshot = storageApi.snapshot<string[]>(STORAGE_KEY);
      if (snapshot.presence === 'present' && Array.isArray(snapshot.value)) {
        setPreferredTabsState(normalizePreferredTabs(snapshot.value));
      }
      if (snapshot.presence !== 'unknown') {
        initializedRef.current = true;
        setLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
      setLoading(false);
    }

    const subscription = storageApi.observe$<string[]>(STORAGE_KEY).subscribe({
      next: snap => {
        if (snap.presence === 'unknown') {
          return;
        }
        if (snap.presence === 'present' && Array.isArray(snap.value)) {
          setPreferredTabsState(normalizePreferredTabs(snap.value));
          setError(null);
        } else if (snap.presence === 'absent') {
          setPreferredTabsState([]);
          setError(null);
        }
        if (!initializedRef.current) {
          initializedRef.current = true;
        }
        setLoading(false);
      },
      error: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load preferences');
        setLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [storageApi]);

  const setPreferredTabs = useCallback(
    async (tabs: string[]) => {
      try {
        const normalizedTabs = normalizePreferredTabs(tabs);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await storageApi.set(STORAGE_KEY, normalizedTabs as any);
        setError(null);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to save preferences';
        setError(errorMsg);
        throw err;
      }
    },
    [storageApi],
  );

  return { preferredTabs, setPreferredTabs, loading, error };
}
